/**
 * Phase 4A playlist sync state machine.
 *
 * Orchestrates PR8 library writes: lease, paging, cursor resume, etag fast
 * path, mark-and-sweep, and syncRun audit. Does not switch My Music reads.
 */
/* global globalThis */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  where,
} from 'firebase/firestore';
import { userPlaylistId } from '../catalog/ids';
import {
  DATA_CLASSES,
  retentionPolicyFor,
} from '../catalog/lifecycle';
import {
  AVAILABILITY_STATES,
  DISPLAY_NAME_SOURCES,
  PLAYLIST_KINDS,
  SYNC_STATUSES,
  buildPlaylistSyncRun,
  buildUserPlaylist,
  buildUserPlaylistItem,
} from '../catalog/schema';
import { db } from '../firebase';
import { recordLibraryMetric } from './libraryMetrics';
import { YOUTUBE_READONLY_SCOPE } from './youtubeAuth';
import {
  buildImportedPlaylistItem,
  buildImportedUserProviderAsset,
  isPlayableImportedTrack,
  LIBRARY_WRITE_BATCH_LIMIT,
  commitUserLibraryWrites,
  userLibraryPaths,
} from './userLibrary';
import {
  isPlaylistRemovalSuppressed,
  refuseRemovedPlaylistWrite,
} from './playlistRemoval';
import {
  YouTubeAuthorizationError,
  attachVideoMetadata,
  fetchPlaylistItemsPage,
  fetchPlaylistMetadata,
  normalizePlaylistItemForLibrary,
} from './youtubePlaylists';

export const PLAYLIST_SYNC_LEASE_MS = 90 * 1000;
export const PLAYLIST_SYNC_LEASE_RENEW_MS = 30 * 1000;
export const PLAYLIST_SYNC_SWEEP_LOADED_RATIO = 0.9;

export const SYNC_OUTCOMES = Object.freeze({
  COMPLETE: 'complete',
  PARTIAL: 'partial',
  FAILED: 'failed',
  UNAUTHORIZED: 'unauthorized',
  UNAVAILABLE: 'unavailable',
  BUSY: 'busy',
  FAST_PATH: 'fast_path',
  REMOVED: 'removed',
});

const PROVIDER = 'youtube';
const YOUTUBE_POLICY = retentionPolicyFor(
  DATA_CLASSES.USER_AUTHORIZED_PROVIDER,
);
const RESUMABLE_STATUSES = new Set([
  SYNC_STATUSES.PARTIAL,
  SYNC_STATUSES.FAILED,
  SYNC_STATUSES.UNAUTHORIZED,
  SYNC_STATUSES.RUNNING,
]);

function requireUid(uid) {
  if (typeof uid !== 'string' || uid.trim().length === 0) {
    throw new TypeError('uid must be a nonempty string');
  }
  return uid;
}

function nonemptyString(value) {
  return typeof value === 'string' && value.trim().length > 0
    ? value
    : null;
}

function resolveNow(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (value && typeof value.toMillis === 'function') {
    return new Date(value.toMillis());
  }
  return new Date();
}

function toMillis(value) {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === 'function') return value.toMillis();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function addPolicyInterval(observedAt, intervalMs) {
  if (!(observedAt instanceof Date) || !Number.isSafeInteger(intervalMs)) {
    return null;
  }
  return new Date(observedAt.getTime() + intervalMs);
}

function providerLifecycle(observedAt) {
  return {
    dataClass: DATA_CLASSES.USER_AUTHORIZED_PROVIDER,
    obtainedVia: 'oauth_user_grant',
    grantScope: YOUTUBE_READONLY_SCOPE,
    lastFetchedAt: observedAt,
    lastRefreshedAt: observedAt,
    refreshDueAt: addPolicyInterval(
      observedAt,
      YOUTUBE_POLICY.refreshIntervalMs,
    ),
    expiresAt: addPolicyInterval(observedAt, YOUTUBE_POLICY.hardExpiryMs),
  };
}

function touchProviderLifecycle(existing, observedAt) {
  const previous = existing && typeof existing === 'object' ? existing : {};
  return {
    dataClass: DATA_CLASSES.USER_AUTHORIZED_PROVIDER,
    obtainedVia: previous.obtainedVia || 'oauth_user_grant',
    grantScope: previous.grantScope || YOUTUBE_READONLY_SCOPE,
    lastFetchedAt: observedAt,
    lastRefreshedAt: previous.lastRefreshedAt ?? null,
    refreshDueAt: previous.refreshDueAt ?? null,
    expiresAt: previous.expiresAt ?? null,
  };
}

function randomHex(bytes) {
  if (!globalThis.crypto?.getRandomValues) {
    throw new Error('Cryptographically secure randomness is unavailable');
  }
  const values = new Uint8Array(bytes);
  globalThis.crypto.getRandomValues(values);
  return Array.from(values, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function createPlaylistSyncClientId() {
  return `tab_${randomHex(8)}`;
}

export function createSyncRunId() {
  return `run_${randomHex(10)}`;
}

export function canSweepPlaylistItems({
  status,
  loadedItemCount,
  knownItemCount,
} = {}) {
  if (status !== SYNC_STATUSES.COMPLETE) return false;
  return hasSufficientPlaylistCoverage({ loadedItemCount, knownItemCount });
}

export function hasSufficientPlaylistCoverage({
  loadedItemCount,
  knownItemCount,
} = {}) {
  if (!Number.isSafeInteger(loadedItemCount) || loadedItemCount < 0) {
    return false;
  }
  if (!Number.isSafeInteger(knownItemCount) || knownItemCount < 0) {
    return false;
  }
  if (knownItemCount <= 0) return loadedItemCount === 0;
  return loadedItemCount >= knownItemCount * PLAYLIST_SYNC_SWEEP_LOADED_RATIO;
}

export function isSuccessfulSyncFresh(lastSuccessfulSyncAt, now, refreshIntervalMs) {
  const lastMs = toMillis(lastSuccessfulSyncAt);
  const current = resolveNow(now);
  const interval = Number.isSafeInteger(refreshIntervalMs)
    ? refreshIntervalMs
    : YOUTUBE_POLICY.refreshIntervalMs;
  if (lastMs == null || !Number.isSafeInteger(interval)) return false;
  return current.getTime() - lastMs < interval;
}

export function isEtagFastPathEligible({
  storedEtag,
  currentEtag,
  storedItemCount,
  currentItemCount,
} = {}) {
  const stored = nonemptyString(storedEtag);
  const current = nonemptyString(currentEtag);
  if (!stored || !current || stored !== current) return false;
  if (
    !Number.isSafeInteger(storedItemCount)
    || !Number.isSafeInteger(currentItemCount)
    || storedItemCount !== currentItemCount
  ) {
    return false;
  }
  // Matching etag + count is a successful revalidation even when the
  // previous sync is older than refreshDueAt. Freshness must not block
  // advancing lifecycle on an unchanged provider revision.
  return true;
}

export function isResumableSync(sync) {
  return Boolean(
    nonemptyString(sync?.cursor)
    && RESUMABLE_STATUSES.has(sync?.status)
    && Number.isSafeInteger(sync?.syncRunSeq)
    && sync.syncRunSeq > 0,
  );
}

export function classifyProviderError(error, { pagesFetched = 0 } = {}) {
  if (error?.code === 'lease_lost' || error?.name === 'PlaylistSyncLeaseLostError') {
    return {
      status: SYNC_STATUSES.PARTIAL,
      failureCode: 'lease_lost',
    };
  }
  if (
    error instanceof YouTubeAuthorizationError
    || error?.name === 'YouTubeAuthorizationError'
    || error?.response?.status === 401
    || error?.code === 'youtube-auth'
  ) {
    return {
      status: SYNC_STATUSES.UNAUTHORIZED,
      failureCode: 'unauthorized',
    };
  }
  const status = error?.response?.status;
  if (
    status === 404
    || error?.code === 'playlist-unavailable'
    || (status === 403 && pagesFetched === 0)
  ) {
    return {
      status: SYNC_STATUSES.UNAVAILABLE,
      failureCode: 'unavailable',
    };
  }
  if (error?.code === 'playlist-incomplete' || error?.name === 'PlaylistIncompleteError') {
    return {
      status: SYNC_STATUSES.PARTIAL,
      failureCode: 'playlist_incomplete',
    };
  }
  if (pagesFetched > 0) {
    return {
      status: SYNC_STATUSES.PARTIAL,
      failureCode: 'network',
    };
  }
  return {
    status: SYNC_STATUSES.FAILED,
    failureCode: error?.code === 'network' ? 'network' : 'failed',
  };
}

function playlistRef(firestore, uid, playlistId) {
  return doc(firestore, 'users', uid, 'playlists', playlistId);
}

function itemCollection(firestore, uid, playlistId) {
  return collection(firestore, 'users', uid, 'playlists', playlistId, 'items');
}

function leasePayload(clientId, now) {
  return {
    ownerClientId: clientId,
    acquiredAt: now,
    leaseExpiresAt: new Date(now.getTime() + PLAYLIST_SYNC_LEASE_MS),
  };
}

function isLeaseHeldByOther(lease, clientId, now) {
  if (!lease?.ownerClientId) return false;
  const expires = toMillis(lease.leaseExpiresAt);
  if (expires == null || expires <= now.getTime()) return false;
  return lease.ownerClientId !== clientId;
}

function isLeaseOwned(lease, clientId, now) {
  if (!lease?.ownerClientId || lease.ownerClientId !== clientId) return false;
  const expires = toMillis(lease.leaseExpiresAt);
  return expires != null && expires > now.getTime();
}

function snapshotTitle(playlist, existing) {
  if (typeof playlist?.title === 'string') return playlist.title;
  return existing?.providerSnapshot?.title || existing?.displayName || '';
}

function assemblePlaylistDocument({
  playlist,
  existing,
  observedAt,
  sync,
  lease,
  knownItemCount,
  loadedItemCount,
  playableItemCount,
  availability,
  lifecycleRefreshed = false,
}) {
  const providerPlaylistId = nonemptyString(playlist?.playlistId)
    || nonemptyString(playlist?.providerPlaylistId)
    || nonemptyString(existing?.providerPlaylistId);
  const userPlaylist = userPlaylistId(PROVIDER, providerPlaylistId);
  const displayNameSource = existing?.displayNameSource === DISPLAY_NAME_SOURCES.USER
    ? DISPLAY_NAME_SOURCES.USER
    : DISPLAY_NAME_SOURCES.PROVIDER;
  const providerTitle = snapshotTitle(playlist, existing);

  return buildUserPlaylist({
    userPlaylistId: userPlaylist,
    kind: PLAYLIST_KINDS.PROVIDER_MIRROR,
    provider: PROVIDER,
    providerPlaylistId,
    providerOwnerChannelId: nonemptyString(playlist?.channelId)
      || nonemptyString(playlist?.providerOwnerChannelId)
      || existing?.providerOwnerChannelId
      || null,
    providerSnapshot: {
      title: providerTitle,
      description: playlist?.description ?? existing?.providerSnapshot?.description ?? null,
      thumbnailUrl: nonemptyString(playlist?.thumbnailUrl)
        || nonemptyString(playlist?.thumbnail)
        || existing?.providerSnapshot?.thumbnailUrl
        || null,
      itemCount: Number.isSafeInteger(knownItemCount)
        ? knownItemCount
        : (Number.isSafeInteger(playlist?.itemCount) ? playlist.itemCount : 0),
      privacyStatus: playlist?.privacyStatus
        ?? existing?.providerSnapshot?.privacyStatus
        ?? null,
      etag: playlist?.etag ?? existing?.providerSnapshot?.etag ?? null,
      observedAt,
    },
    displayName: displayNameSource === DISPLAY_NAME_SOURCES.USER
      ? existing.displayName
      : providerTitle,
    displayNameSource,
    knownItemCount: Number.isSafeInteger(knownItemCount)
      ? knownItemCount
      : (Number.isSafeInteger(playlist?.itemCount) ? playlist.itemCount : 0),
    loadedItemCount: Number.isSafeInteger(loadedItemCount) ? loadedItemCount : 0,
    playableItemCount: Number.isSafeInteger(playableItemCount) ? playableItemCount : 0,
    availability: availability || AVAILABILITY_STATES.AVAILABLE,
    sync,
    lease,
    lifecycle: lifecycleRefreshed
      ? providerLifecycle(observedAt)
      : touchProviderLifecycle(existing?.lifecycle, observedAt),
    importedAt: existing?.importedAt ?? observedAt,
    createdAt: existing?.createdAt ?? observedAt,
    updatedAt: observedAt,
  });
}

async function readPlaylist(firestore, uid, playlistId) {
  const snapshot = await getDoc(playlistRef(firestore, uid, playlistId));
  if (!snapshot?.exists?.()) return null;
  return snapshot.data() || null;
}

async function writePlaylist(firestore, uid, document) {
  refuseRemovedPlaylistWrite(document.userPlaylistId);
  await commitUserLibraryWrites([{
    uid,
    path: userLibraryPaths(uid).playlist(document.userPlaylistId),
    segments: ['playlists', document.userPlaylistId],
    data: document,
  }], { firestore });
}

async function writeSyncRun(firestore, uid, playlistId, document) {
  refuseRemovedPlaylistWrite(playlistId);
  const syncRunId = document.syncRunDocId;
  const {
    syncRunDocId: _ignored,
    ...payload
  } = document;
  await commitUserLibraryWrites([{
    uid,
    path: `${userLibraryPaths(uid).playlist(playlistId)}/syncRuns/${syncRunId}`,
    segments: ['playlists', playlistId, 'syncRuns', syncRunId],
    data: payload,
  }], { firestore });
}

function buildAuditRecord({
  runSeq,
  startedAt,
  completedAt,
  status,
  pagesFetched,
  itemsSeen,
  itemsCreated,
  itemsUpdated,
  itemsRemoved,
  failureCode,
  clientId,
  observedAt,
  syncRunId,
}) {
  return {
    ...buildPlaylistSyncRun({
      runSeq,
      startedAt,
      completedAt,
      status,
      pagesFetched,
      itemsSeen,
      itemsCreated,
      itemsUpdated,
      itemsRemoved,
      failureCode,
      clientId,
      lifecycle: providerLifecycle(observedAt),
    }),
    syncRunDocId: syncRunId,
  };
}

function playlistFromExisting(current, fallback) {
  return {
    playlistId: current?.providerPlaylistId || fallback?.playlistId,
    title: current?.providerSnapshot?.title ?? fallback?.title,
    description: current?.providerSnapshot?.description ?? fallback?.description,
    thumbnail: current?.providerSnapshot?.thumbnailUrl ?? fallback?.thumbnail,
    itemCount: current?.knownItemCount ?? fallback?.itemCount,
    privacyStatus: current?.providerSnapshot?.privacyStatus ?? fallback?.privacyStatus,
    channelId: current?.providerOwnerChannelId ?? fallback?.channelId,
    etag: current?.providerSnapshot?.etag ?? fallback?.etag,
    source: 'youtube',
    ...fallback,
  };
}

export async function acquirePlaylistSyncLease({
  uid,
  playlistId,
  clientId,
  now,
  firestore = db,
  existing,
  playlist,
  sync,
  knownItemCount,
  loadedItemCount,
  playableItemCount,
} = {}) {
  const owner = requireUid(uid);
  const observed = resolveNow(now);
  return runTransaction(firestore, async (tx) => {
    const ref = playlistRef(firestore, owner, playlistId);
    if (isPlaylistRemovalSuppressed(playlistId)) {
      return { status: 'removed', playlist: null };
    }
    const snapshot = await tx.get(ref);
    const current = snapshot?.exists?.() ? snapshot.data() : existing || null;
    if (isLeaseHeldByOther(current?.lease, clientId, observed)) {
      return { status: 'busy', playlist: current };
    }
    const nextLease = leasePayload(clientId, observed);
    const document = assemblePlaylistDocument({
      playlist: playlistFromExisting(current, playlist),
      existing: current,
      observedAt: observed,
      sync: sync || current?.sync || {
        status: SYNC_STATUSES.QUEUED,
        syncRunId: null,
        syncRunSeq: 0,
        startedAt: null,
        completedAt: null,
        lastSuccessfulSyncAt: null,
        providerRevision: null,
        cursor: null,
        failureCode: null,
        sweepCompletedAt: null,
      },
      lease: nextLease,
      knownItemCount,
      loadedItemCount,
      playableItemCount,
    });
    tx.set(ref, document);
    return { status: 'acquired', playlist: document, lease: nextLease };
  });
}

export async function renewPlaylistSyncLease({
  uid,
  playlistId,
  clientId,
  now,
  firestore = db,
} = {}) {
  const owner = requireUid(uid);
  const observed = resolveNow(now);
  return runTransaction(firestore, async (tx) => {
    const ref = playlistRef(firestore, owner, playlistId);
    if (isPlaylistRemovalSuppressed(playlistId)) {
      return { status: 'removed', playlist: null };
    }
    const snapshot = await tx.get(ref);
    const current = snapshot?.exists?.() ? snapshot.data() : null;
    if (!isLeaseOwned(current?.lease, clientId, observed)) {
      return { status: 'lost', playlist: current };
    }
    const nextLease = leasePayload(clientId, observed);
    const document = assemblePlaylistDocument({
      playlist: playlistFromExisting(current),
      existing: current,
      observedAt: observed,
      sync: current.sync,
      lease: nextLease,
      knownItemCount: current.knownItemCount,
      loadedItemCount: current.loadedItemCount,
      playableItemCount: current.playableItemCount,
      availability: current.availability,
    });
    tx.set(ref, document);
    return { status: 'renewed', playlist: document, lease: nextLease };
  });
}

export async function releasePlaylistSyncLease({
  uid,
  playlistId,
  clientId,
  now,
  firestore = db,
} = {}) {
  const owner = requireUid(uid);
  const observed = resolveNow(now);
  return runTransaction(firestore, async (tx) => {
    const ref = playlistRef(firestore, owner, playlistId);
    const snapshot = await tx.get(ref);
    if (!snapshot?.exists?.()) return { status: 'missing' };
    const current = snapshot.data();
    if (current?.lease?.ownerClientId !== clientId) {
      return { status: 'not-owner', playlist: current };
    }
    const document = assemblePlaylistDocument({
      playlist: playlistFromExisting(current),
      existing: current,
      observedAt: observed,
      sync: current.sync,
      lease: null,
      knownItemCount: current.knownItemCount,
      loadedItemCount: current.loadedItemCount,
      playableItemCount: current.playableItemCount,
      availability: current.availability,
    });
    tx.set(ref, document);
    return { status: 'released', playlist: document };
  });
}

async function defaultFetchPage(playlistId, cursor) {
  const page = await fetchPlaylistItemsPage(playlistId, cursor || '');
  const tracks = (page.items || [])
    .map((item) => normalizePlaylistItemForLibrary(item, { playlistId }))
    .filter(Boolean);
  return {
    tracks: await attachVideoMetadata(tracks),
    nextPageToken: page.nextPageToken || null,
  };
}

async function loadExistingItems(firestore, uid, playlistId) {
  const snapshot = await getDocs(itemCollection(firestore, uid, playlistId));
  const docs = snapshot?.docs || [];
  const byId = new Map();
  docs.forEach((entry) => {
    const data = entry.data();
    byId.set(entry.id || data.userPlaylistItemId, data);
  });
  return byId;
}

async function loadSweepCandidates(firestore, uid, playlistId, runSeq) {
  const snapshot = await getDocs(query(
    itemCollection(firestore, uid, playlistId),
    where('lastSeenSyncRunSeq', '<', runSeq),
  ));
  return (snapshot?.docs || []).map((entry) => entry.data());
}

function itemWritesForTracks({
  uid,
  playlistId,
  tracks,
  observedAt,
  runSeq,
  existingItems = new Map(),
}) {
  const writes = [];
  const assetKeys = new Set();
  tracks.forEach((track, index) => {
    const draft = buildImportedPlaylistItem({
      track: {
        ...track,
        lastSeenSyncRunSeq: runSeq,
        removedFromPlaylistAt: null,
      },
      index,
      observedAt,
    });
    const previous = existingItems.get(draft.userPlaylistItemId);
    const item = previous
      ? buildImportedPlaylistItem({
        track: {
          ...track,
          lastSeenSyncRunSeq: runSeq,
          removedFromPlaylistAt: null,
        },
        index,
        observedAt,
        previous,
      })
      : draft;
    writes.push({
      uid,
      path: userLibraryPaths(uid).item(playlistId, item.userPlaylistItemId),
      segments: ['playlists', playlistId, 'items', item.userPlaylistItemId],
      data: item,
      kind: 'item',
      item,
    });
    const asset = buildImportedUserProviderAsset({
      track,
      observedAt,
      previous,
    });
    if (assetKeys.has(asset.providerAssetKey)) return;
    assetKeys.add(asset.providerAssetKey);
    writes.push({
      uid,
      path: userLibraryPaths(uid).asset(asset.providerAssetKey),
      segments: ['assets', asset.providerAssetKey],
      data: asset,
      kind: 'asset',
      asset,
    });
  });
  return writes;
}

function softRemoveWrite(uid, playlistId, existing, completedAt) {
  const item = buildUserPlaylistItem({
    userPlaylistItemId: existing.userPlaylistItemId,
    occurrenceIdSource: existing.occurrenceIdSource,
    provider: existing.provider,
    providerPlaylistItemId: existing.providerPlaylistItemId,
    providerAssetKey: existing.providerAssetKey,
    providerAssetId: existing.providerAssetId,
    position: existing.position,
    observation: existing.observation,
    availability: AVAILABILITY_STATES.REMOVED,
    playbackEligibility: existing.playbackEligibility,
    lastKnownGoodObservation: existing.lastKnownGoodObservation ?? null,
    addedToPlaylistAt: existing.addedToPlaylistAt,
    lastSeenSyncRunSeq: existing.lastSeenSyncRunSeq,
    removedFromPlaylistAt: completedAt,
    lifecycle: existing.lifecycle,
    createdAt: existing.createdAt,
    updatedAt: completedAt,
  });
  return {
    uid,
    path: userLibraryPaths(uid).item(playlistId, item.userPlaylistItemId),
    segments: ['playlists', playlistId, 'items', item.userPlaylistItemId],
    data: item,
  };
}

export async function syncImportedPlaylist(uid, playlist, options = {}) {
  const owner = requireUid(uid);
  const providerPlaylistId = nonemptyString(playlist?.playlistId)
    || nonemptyString(playlist?.providerPlaylistId);
  if (!providerPlaylistId) {
    throw new TypeError('playlist.playlistId is required');
  }

  const startedMs = Date.now();
  const firestore = options.firestore || db;
  const clientId = nonemptyString(options.clientId) || createPlaylistSyncClientId();
  const observedAt = resolveNow(options.now || options.observedAt);
  const catalogId = userPlaylistId(PROVIDER, providerPlaylistId);
  const fetchMetadata = options.fetchPlaylistMetadata || fetchPlaylistMetadata;
  const fetchPage = options.fetchPlaylistItemsPage || defaultFetchPage;
  const persistLibraryWrites = options.commitUserLibraryWrites || commitUserLibraryWrites;
  const yieldBetweenBatches = options.yieldBetweenBatches || (() => Promise.resolve());

  const stats = {
    pagesFetched: 0,
    itemsSeen: 0,
    itemsCreated: 0,
    itemsUpdated: 0,
    itemsRemoved: 0,
    playlistWrites: 0,
    itemWrites: 0,
    assetWrites: 0,
    batchCommits: 0,
    maxBatchWrites: 0,
    metadataFetches: 0,
    pageFetches: 0,
    swept: false,
    transitions: [SYNC_STATUSES.IDLE],
  };

  if (isPlaylistRemovalSuppressed(catalogId) || isPlaylistRemovalSuppressed(providerPlaylistId)) {
    recordLibraryMetric({
      operation: 'sync',
      catalogPlaylistId: catalogId,
      outcome: SYNC_OUTCOMES.REMOVED,
      providerPagesFetched: 0,
      providerItemsFetched: 0,
      itemDocsAttempted: 0,
      assetDocsAttempted: 0,
      batchCount: 0,
      elapsedMs: Date.now() - startedMs,
    });
    return {
      outcome: SYNC_OUTCOMES.REMOVED,
      status: SYNC_OUTCOMES.REMOVED,
      uid: owner,
      playlistId: catalogId,
      clientId,
      swept: false,
      stats,
    };
  }

  const emitSyncMetric = (outcome) => {
    recordLibraryMetric({
      operation: 'sync',
      catalogPlaylistId: catalogId,
      outcome: outcome || null,
      providerPagesFetched: stats.pagesFetched,
      providerItemsFetched: stats.itemsSeen,
      itemDocsAttempted: stats.itemWrites,
      assetDocsAttempted: stats.assetWrites,
      batchCount: stats.batchCommits,
      elapsedMs: Date.now() - startedMs,
    });
  };

  let existing = options.existingPlaylist
    || await readPlaylist(firestore, owner, catalogId);
  const existingItems = await loadExistingItems(firestore, owner, catalogId);
  const expectedKnown = Number.isSafeInteger(playlist?.itemCount)
    ? playlist.itemCount
    : (Number.isSafeInteger(existing?.knownItemCount) ? existing.knownItemCount : 0);
  const persistedPositions = [...existingItems.values()]
    .map((item) => item.position)
    .filter((value) => Number.isSafeInteger(value));
  const firstPageMissing = expectedKnown > 0 && (
    existingItems.size === 0
    || (persistedPositions.length > 0 && Math.min(...persistedPositions) > 0)
  );
  const resumable = isResumableSync(existing?.sync) && !firstPageMissing;
  const runSeq = resumable ? existing.sync.syncRunSeq : (existing?.sync?.syncRunSeq || 0) + 1;
  const syncRunId = resumable && nonemptyString(existing.sync.syncRunId)
    ? existing.sync.syncRunId
    : createSyncRunId();
  const startedAt = resumable && existing.sync.startedAt
    ? existing.sync.startedAt
    : observedAt;
  let cursor = resumable ? existing.sync.cursor : null;
  let loadedItemCount = resumable ? (existing.loadedItemCount || 0) : 0;
  let playableItemCount = resumable ? (existing.playableItemCount || 0) : 0;
  let lease = leasePayload(clientId, observedAt);
  let lastRenewalMs = observedAt.getTime();

  const existedAtStart = new Set(existingItems.keys());
  const seenThisRun = new Set();
  if (resumable) {
    existingItems.forEach((item, itemId) => {
      if (item.lastSeenSyncRunSeq === runSeq) {
        seenThisRun.add(itemId);
      }
    });
    stats.itemsSeen = seenThisRun.size;
  }

  const acquire = await acquirePlaylistSyncLease({
    uid: owner,
    playlistId: catalogId,
    clientId,
    now: observedAt,
    firestore,
    existing,
    playlist: { ...playlist, playlistId: providerPlaylistId },
    sync: {
      status: SYNC_STATUSES.QUEUED,
      syncRunId,
      syncRunSeq: runSeq,
      startedAt,
      completedAt: null,
      lastSuccessfulSyncAt: existing?.sync?.lastSuccessfulSyncAt ?? null,
      providerRevision: existing?.sync?.providerRevision ?? null,
      cursor,
      failureCode: null,
      sweepCompletedAt: existing?.sync?.sweepCompletedAt ?? null,
    },
    knownItemCount: Number.isSafeInteger(playlist?.itemCount)
      ? playlist.itemCount
      : existing?.knownItemCount,
    loadedItemCount,
    playableItemCount,
  });
  if (acquire.status === 'removed') {
    emitSyncMetric(SYNC_OUTCOMES.REMOVED);
    return {
      outcome: SYNC_OUTCOMES.REMOVED,
      status: SYNC_OUTCOMES.REMOVED,
      uid: owner,
      playlistId: catalogId,
      clientId,
      swept: false,
      stats,
    };
  }
  if (acquire.status === 'busy') {
    emitSyncMetric(SYNC_OUTCOMES.BUSY);
    return {
      outcome: SYNC_OUTCOMES.BUSY,
      status: acquire.playlist?.sync?.status || SYNC_STATUSES.QUEUED,
      uid: owner,
      playlistId: catalogId,
      clientId,
      swept: false,
      stats,
    };
  }
  if (acquire.lease) lease = acquire.lease;
  if (acquire.playlist) existing = acquire.playlist;
  stats.transitions.push(SYNC_STATUSES.QUEUED, SYNC_STATUSES.RUNNING);

  const persistPlaylistState = async ({
    provider,
    sync,
    nextLease = lease,
    knownItemCount,
    loaded,
    playable,
    availability,
    at = observedAt,
    lifecycleRefreshed = false,
  }) => {
    const document = assemblePlaylistDocument({
      playlist: provider,
      existing,
      observedAt: at,
      sync,
      lease: nextLease,
      knownItemCount,
      loadedItemCount: loaded,
      playableItemCount: playable,
      availability,
      lifecycleRefreshed,
    });
    await writePlaylist(firestore, owner, document);
    stats.playlistWrites += 1;
    return document;
  };

  let providerMeta = {
    ...playlist,
    playlistId: providerPlaylistId,
  };

  const runningSync = (overrides = {}) => ({
    status: SYNC_STATUSES.RUNNING,
    syncRunId,
    syncRunSeq: runSeq,
    startedAt,
    completedAt: null,
    lastSuccessfulSyncAt: existing?.sync?.lastSuccessfulSyncAt ?? null,
    providerRevision: providerMeta.etag ?? existing?.sync?.providerRevision ?? null,
    cursor,
    failureCode: null,
    sweepCompletedAt: existing?.sync?.sweepCompletedAt ?? null,
    ...overrides,
  });

  let playlistDoc = await persistPlaylistState({
    provider: providerMeta,
    sync: runningSync(),
    loaded: loadedItemCount,
    playable: playableItemCount,
    knownItemCount: Number.isSafeInteger(providerMeta.itemCount)
      ? providerMeta.itemCount
      : existing?.knownItemCount,
  });

  const ensureLease = async (now) => {
    const current = await readPlaylist(firestore, owner, catalogId);
    if (!isLeaseOwned(current?.lease, clientId, now)) {
      const error = new Error('Playlist sync lease lost');
      error.code = 'lease_lost';
      error.name = 'PlaylistSyncLeaseLostError';
      throw error;
    }
    if (now.getTime() - lastRenewalMs < PLAYLIST_SYNC_LEASE_RENEW_MS) {
      return;
    }
    const renewal = await renewPlaylistSyncLease({
      uid: owner,
      playlistId: catalogId,
      clientId,
      now,
      firestore,
    });
    if (renewal.status !== 'renewed') {
      const error = new Error('Playlist sync lease lost');
      error.code = 'lease_lost';
      error.name = 'PlaylistSyncLeaseLostError';
      throw error;
    }
    lease = renewal.lease;
    lastRenewalMs = now.getTime();
  };

  const finish = async ({
    status,
    failureCode = null,
    provider = providerMeta,
    knownItemCount,
    loaded = loadedItemCount,
    playable = playableItemCount,
    availability,
    sweep = false,
    outcome,
    at = observedAt,
  }) => {
    const completedAt = status === SYNC_STATUSES.RUNNING ? null : at;
    let itemsRemoved = stats.itemsRemoved;
    let sweepCompletedAt = existing?.sync?.sweepCompletedAt ?? null;
    let allowSweep = sweep;

    if (allowSweep && canSweepPlaylistItems({
      status,
      loadedItemCount: loaded,
      knownItemCount: Number.isSafeInteger(knownItemCount)
        ? knownItemCount
        : (Number.isSafeInteger(provider.itemCount) ? provider.itemCount : 0),
    })) {
      try {
        await ensureLease(at);
      } catch (error) {
        if (error?.code !== 'lease_lost') throw error;
        allowSweep = false;
      }
    }

    if (allowSweep && canSweepPlaylistItems({
      status,
      loadedItemCount: loaded,
      knownItemCount: Number.isSafeInteger(knownItemCount)
        ? knownItemCount
        : (Number.isSafeInteger(provider.itemCount) ? provider.itemCount : 0),
    })) {
      const stale = await loadSweepCandidates(firestore, owner, catalogId, runSeq);
      const removals = stale
        .filter((item) => item && item.removedFromPlaylistAt == null)
        .map((item) => softRemoveWrite(owner, catalogId, item, at));
      if (removals.length > 0) {
        stats.maxBatchWrites = Math.max(
          stats.maxBatchWrites,
          Math.min(removals.length, LIBRARY_WRITE_BATCH_LIMIT),
        );
        refuseRemovedPlaylistWrite(catalogId);
        stats.batchCommits += await persistLibraryWrites(removals, { firestore });
      }
      itemsRemoved += removals.length;
      stats.itemsRemoved = itemsRemoved;
      stats.swept = true;
      sweepCompletedAt = at;
    }

    const restingStatus = status === SYNC_STATUSES.COMPLETE
      ? (stats.swept ? SYNC_STATUSES.IDLE : SYNC_STATUSES.COMPLETE)
      : status;
    const release = await releasePlaylistSyncLease({
      uid: owner,
      playlistId: catalogId,
      clientId,
      now: at,
      firestore,
    });
    const nextLease = release.status === 'released'
      ? null
      : (release.playlist?.lease ?? null);

    playlistDoc = await persistPlaylistState({
      provider,
      nextLease,
      knownItemCount,
      loaded,
      playable,
      availability,
      at,
      lifecycleRefreshed: status === SYNC_STATUSES.COMPLETE,
      sync: {
        status: restingStatus,
        syncRunId,
        syncRunSeq: runSeq,
        startedAt,
        completedAt,
        lastSuccessfulSyncAt: status === SYNC_STATUSES.COMPLETE
          ? at
          : (existing?.sync?.lastSuccessfulSyncAt ?? null),
        providerRevision: provider.etag ?? null,
        cursor: status === SYNC_STATUSES.COMPLETE ? null : cursor,
        failureCode,
        sweepCompletedAt,
      },
    });

    const audit = buildAuditRecord({
      runSeq,
      startedAt,
      completedAt,
      status,
      pagesFetched: stats.pagesFetched,
      itemsSeen: stats.itemsSeen,
      itemsCreated: stats.itemsCreated,
      itemsUpdated: stats.itemsUpdated,
      itemsRemoved,
      failureCode,
      clientId,
      observedAt: at,
      syncRunId,
    });
    await writeSyncRun(firestore, owner, catalogId, audit);
    stats.transitions.push(status);
    if (restingStatus !== status) stats.transitions.push(restingStatus);

    emitSyncMetric(outcome || status);
    return {
      outcome: outcome || status,
      status,
      restingStatus,
      uid: owner,
      playlistId: catalogId,
      clientId,
      syncRunId,
      runSeq,
      cursor: playlistDoc.sync.cursor,
      swept: stats.swept,
      playlist: playlistDoc,
      syncRun: audit,
      stats,
    };
  };

  try {
    providerMeta = {
      ...providerMeta,
      ...(await fetchMetadata(providerPlaylistId)),
      playlistId: providerPlaylistId,
    };
    stats.metadataFetches += 1;
    await ensureLease(observedAt);

    const knownItemCount = Number.isSafeInteger(providerMeta.itemCount)
      ? providerMeta.itemCount
      : 0;

    const persistedItemCount = existingItems.size;
    if (
      hasSufficientPlaylistCoverage({
        loadedItemCount: persistedItemCount,
        knownItemCount,
      })
      && isEtagFastPathEligible({
        storedEtag: existing?.providerSnapshot?.etag || existing?.sync?.providerRevision,
        currentEtag: providerMeta.etag,
        storedItemCount: existing?.knownItemCount,
        currentItemCount: knownItemCount,
        lastSuccessfulSyncAt: existing?.sync?.lastSuccessfulSyncAt,
        now: observedAt,
      })
    ) {
      return finish({
        status: SYNC_STATUSES.COMPLETE,
        provider: providerMeta,
        knownItemCount,
        loaded: existing?.loadedItemCount || 0,
        playable: existing?.playableItemCount || 0,
        sweep: false,
        outcome: SYNC_OUTCOMES.FAST_PATH,
      });
    }

    let pageToken = cursor;
    let complete = false;

    while (!complete) {
      await ensureLease(observedAt);
      const page = await fetchPage(providerPlaylistId, pageToken || '');
      stats.pageFetches += 1;
      stats.pagesFetched += 1;
      const tracks = Array.isArray(page?.tracks) ? page.tracks : [];

      for (let index = 0; index < tracks.length; index += 1) {
        const track = tracks[index];
        if (!nonemptyString(track?.playlistItemId) && !nonemptyString(track?.providerOccurrenceId)) {
          continue;
        }
        if (!nonemptyString(track?.videoId) && !nonemptyString(track?.id)) {
          continue;
        }
        const identity = buildImportedPlaylistItem({
          track: { ...track, lastSeenSyncRunSeq: runSeq },
          observedAt,
        });
        if (seenThisRun.has(identity.userPlaylistItemId)) {
          continue;
        }
        seenThisRun.add(identity.userPlaylistItemId);
        if (existedAtStart.has(identity.userPlaylistItemId)) {
          stats.itemsUpdated += 1;
        } else {
          stats.itemsCreated += 1;
          existingItems.set(identity.userPlaylistItemId, identity);
        }
        stats.itemsSeen += 1;
        if (isPlayableImportedTrack(track)) {
          playableItemCount += 1;
        }
      }
      loadedItemCount = seenThisRun.size;

      const writes = itemWritesForTracks({
        uid: owner,
        playlistId: catalogId,
        tracks: tracks.filter((track) => (
          (nonemptyString(track.playlistItemId) || nonemptyString(track.providerOccurrenceId))
          && (nonemptyString(track.videoId) || nonemptyString(track.id))
        )),
        observedAt,
        runSeq,
        existingItems,
      });
      stats.itemWrites += writes.filter((write) => write.kind === 'item').length;
      stats.assetWrites += writes.filter((write) => write.kind === 'asset').length;
      if (writes.length > 0) {
        stats.maxBatchWrites = Math.max(
          stats.maxBatchWrites,
          Math.min(writes.length, LIBRARY_WRITE_BATCH_LIMIT),
        );
      }

      const nextCursor = page?.nextPageToken || null;

      const checkpoint = assemblePlaylistDocument({
        playlist: providerMeta,
        existing,
        observedAt,
        lease,
        knownItemCount,
        loadedItemCount,
        playableItemCount,
        sync: runningSync({
          cursor: nextCursor,
          providerRevision: providerMeta.etag ?? null,
        }),
      });
      writes.push({
        uid: owner,
        path: userLibraryPaths(owner).playlist(catalogId),
        segments: ['playlists', catalogId],
        data: checkpoint,
      });
      refuseRemovedPlaylistWrite(catalogId);
      stats.batchCommits += await persistLibraryWrites(writes, { firestore });
      stats.playlistWrites += 1;
      cursor = nextCursor;
      pageToken = nextCursor;
      complete = !nextCursor;
      playlistDoc = checkpoint;
      await yieldBetweenBatches();
    }

    playableItemCount = tracksPlayableFromStats(playableItemCount, stats.itemsSeen);
    const covered = hasSufficientPlaylistCoverage({
      loadedItemCount,
      knownItemCount,
    });
    return finish({
      status: covered ? SYNC_STATUSES.COMPLETE : SYNC_STATUSES.PARTIAL,
      provider: providerMeta,
      knownItemCount,
      loaded: loadedItemCount,
      playable: playableItemCount,
      sweep: covered,
      outcome: covered ? SYNC_OUTCOMES.COMPLETE : SYNC_OUTCOMES.PARTIAL,
    });
  } catch (error) {
    if (error?.code === 'playlist-removed') {
      emitSyncMetric(SYNC_OUTCOMES.REMOVED);
      return {
        outcome: SYNC_OUTCOMES.REMOVED,
        status: SYNC_OUTCOMES.REMOVED,
        uid: owner,
        playlistId: catalogId,
        clientId,
        swept: false,
        stats,
      };
    }
    const classified = classifyProviderError(error, {
      pagesFetched: stats.pagesFetched,
    });
    const knownItemCount = Number.isSafeInteger(providerMeta.itemCount)
      ? providerMeta.itemCount
      : existing?.knownItemCount;
    return finish({
      status: classified.status,
      failureCode: classified.failureCode,
      provider: providerMeta,
      knownItemCount,
      loaded: loadedItemCount,
      playable: playableItemCount,
      availability: classified.status === SYNC_STATUSES.UNAVAILABLE
        ? AVAILABILITY_STATES.DELETED
        : AVAILABILITY_STATES.AVAILABLE,
      sweep: false,
      outcome: classified.status,
    });
  }
}

function tracksPlayableFromStats(playableItemCount, itemsSeen) {
  if (!Number.isSafeInteger(playableItemCount) || playableItemCount > itemsSeen) {
    return itemsSeen;
  }
  return playableItemCount;
}
